// authService.js
import { loginRequest, apiConfig } from "./authConfig";
import { msalInstance } from "./msalClient";
import { Environment } from "../environments/Environment";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

class AuthService {
  constructor() {
    this.instance = msalInstance;
    this.redirectPromise = null;

    // Store the initialization promise and process redirect responses if present.
    this.initPromise = this.instance.initialize().then(() => {
      this.redirectPromise = this.instance.handleRedirectPromise().then((response) => {
        if (response) {
          return this.handleRedirectResponse(response);
        }
        return response;
      });
    });
  }

  async ensureInitialized() {
    await this.initPromise;
    if (this.redirectPromise) {
      await this.redirectPromise;
    }
  }

  async handleRedirectResponse(response) {
    const account = response.account;
    const claims = account && account.idTokenClaims ? account.idTokenClaims : {};

    console.log("=== MSAL REDIRECT DEBUG ===");
    console.log("Account username:", account?.username);
    console.log("Account email:", account?.idTokenClaims?.email);
    console.log("Claims keys:", Object.keys(claims));
    console.log("Groups claim:", claims.groups);
    console.log("Groups claim type:", typeof claims.groups);
    console.log("Expected admin group:", Environment.azureEntraAppFulcrumAdminGroup);
    console.log("Expected users group:", Environment.azureEntraAppFulcrumUsersGroup);
    console.log("===========================");

    const groups = Array.isArray(claims.groups) ? claims.groups : [];
    const hasRequiredGroup = groups.includes(Environment.azureEntraAppFulcrumAdminGroup)
        || groups.includes(Environment.azureEntraAppFulcrumUsersGroup);

    if (!hasRequiredGroup) {
      console.error("AUTH FAILED: User is NOT in required group.", {
        username: account?.username,
        email: account?.idTokenClaims?.email,
        groups,
      });
      await this.instance.logoutRedirect({ account });
      throw new Error("You are not authorized to use this application.");
    }

    this.instance.setActiveAccount(account);
    return response;
  }

  // ---- LOGIN ----
  async login() {
    await this.ensureInitialized();
    return this.instance.loginRedirect(loginRequest);
  }

  // ---- LOGOUT ----
  async logout() {
    return this.instance.logoutRedirect(); // or logoutPopup()
  }

  async isCurrentUserAdmin() {
    const account = this.instance.getActiveAccount();

    const claims = account && account.idTokenClaims ? account.idTokenClaims : {};

    const groups = Array.isArray(claims.groups) ? claims.groups : [];

    return groups.includes(Environment.azureEntraAppFulcrumAdminGroup);
  }

  // ---- GET ACTIVE ACCOUNT ----
  getActiveAccount() {
    const accounts = this.instance.getAllAccounts();
    return accounts && accounts.length ? accounts[0] : null;
  }

  // ---- CHECK SIGN-IN STATUS ----
  isSignedIn() {
    return !!this.getActiveAccount();
  }

  // ---- ACQUIRE TOKEN ----
  async acquireToken() {
    await this.ensureInitialized();
    
    const account = this.getActiveAccount();

    if (!account) {
      throw new Error("No active account");
    }

    try {
      const res = await this.instance.acquireTokenSilent({
        ...loginRequest,
        account,
        scopes: [apiConfig.scope],
      });
      return res.accessToken;

    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // fallback to popup for token acquisition when interaction is required
        const res = await this.instance.acquireTokenPopup({
          ...loginRequest,
          scopes: [apiConfig.scope],
        });
        return res.accessToken;
      }
      throw err;
    }
  }
}

// Export a singleton instance
const authService = new AuthService();
export default authService;
